import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import { toast } from '../../toasts';
import relWarrantyAssetService from '../../services/relWarrantyAssets.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button, ExportModal, buildXlsxExport, exportAmount } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';

function formatMoney(currency, amount) {
	if (amount === null || amount === undefined) {
		return '';
	}
	return `${showCurrency(currency) || ''} ${Number(amount).toLocaleString('es-UY')}`.trim();
}

// A cash ('efectivo') warranty has no debt/asset, so its currency lives on the
// warranty itself; the other two options carry it on the guaranteed record.
const warrantyCurrency = (warranty) =>
	warranty?.debt?.currency || warranty?.asset?.currency || warranty?.currency || null;

function CreditWarranties({ creditId, rows = [], loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();
	const [deletingWarranty, setDeletingWarranty] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	// These rows have no name column; `option` is the only human-readable label
	// (the debt/asset IdChips are each empty for a cash warranty).
	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'option',
		accessors: { option: (relWarranty) => relWarranty.warranty?.option },
	});

	const openWarrantyEntity = (relWarranty) => {
		const debtId = relWarranty.warranty?.debt?._id;
		const assetId = relWarranty.warranty?.asset?._id;
		if (debtId) navigate(`/AdminScreen/debts/debt/${debtId}`);
		else if (assetId) navigate(`/AdminScreen/assets/asset/${assetId}`);
	};

	const handleCreateWarranty = () => {
		const returnTo = encodeURIComponent(location.pathname + location.search);
		navigate(`/AdminScreen/credits/credit/${creditId}/newWarranty?returnTo=${returnTo}`);
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((relWarranty) => {
			const key = warrantyCurrency(relWarranty.warranty) || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (relWarranty.warranty?.amount || 0));
		});
		return [
			{ label: t('entity.warranty.plural'), value: rows.length },
			{
				label: t('field.totalGuaranteed'),
				value:
					[...totalByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	async function handleConfirmDelete() {
		try {
			await relWarrantyAssetService.delete(deletingWarranty._id);
			toast.success(t('msg.warrantyDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingWarranty(null);
		}
	}

	const columns = [
		{ key: 'option', header: t('field.option'), sortable: true, render: (relWarranty) => relWarranty.warranty?.option },
		{
			key: 'debt',
			header: t('entity.debt'),
			render: (relWarranty) =>
				relWarranty.warranty?.debt?.customId ? <IdChip>{relWarranty.warranty.debt.customId}</IdChip> : null,
		},
		{
			key: 'asset',
			header: t('entity.asset'),
			render: (relWarranty) =>
				relWarranty.warranty?.asset?.customId ? <IdChip>{relWarranty.warranty.asset.customId}</IdChip> : null,
		},
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (relWarranty) => (
				<span className='tw:font-mono tw:tabular-nums'>
					{formatMoney(warrantyCurrency(relWarranty.warranty), relWarranty.warranty?.amount)}
				</span>
			),
		},
	];

	const exportFields = [
		{ key: 'option', label: t('field.option'), locked: true },
		{ key: 'debt', label: t('entity.debt') },
		{ key: 'asset', label: t('entity.asset') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'amount', label: t('field.amount') },
	];

	const exportGetters = {
		option: (relWarranty) => relWarranty.warranty?.option || '',
		debt: (relWarranty) => relWarranty.warranty?.debt?.customId || '',
		asset: (relWarranty) => relWarranty.warranty?.asset?.customId || '',
		currency: (relWarranty) => warrantyCurrency(relWarranty.warranty) || '',
		amount: (relWarranty) => exportAmount(relWarranty.warranty?.amount),
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Garantias',
			filePrefix: 'garantias',
		});

	const renderRowActions = (relWarranty) => (
		<RowActionsMenu
			items={[{ label: t('action.delete'), onClick: () => setDeletingWarranty(relWarranty), danger: true }]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={handleCreateWarranty}>
					{t('title.newWarranty')}
				</Button>
			</div>
			<Section title={t('entity.warranty.plural')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(relWarranty) => openWarrantyEntity(relWarranty)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron garantías para este crédito.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle={t('entity.warranty.plural')}
				onExport={handleExport}
			/>

			<ConfirmationModal
				show={Boolean(deletingWarranty)}
				handleClose={() => setDeletingWarranty(null)}
				handleConfirm={handleConfirmDelete}
				title={`Eliminar ${t('entity.warranty')}`}
				description={t('msg.confirmDeleteWarranty')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default CreditWarranties;
